import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation } from "react-router";
import { EnumUserUserType } from "gql/generated";
import { useAppContext } from "useAppContext";
import {
  onWorkbenchEvent,
  type WorkbenchSessionEvent,
} from "./workbenchEvents";

const SESSION_KEY = "wa_workbench";
const EVENTS_KEY = "wa_workbench_events";
const MAX_EVENTS = 50;

export type WorkbenchSession = {
  flagKey: string;
  featureName: string;
  chatId: string;
  /** Absent for admin-user scoped features (no org in the route). */
  organizationId?: string | null;
  organizationName?: string | null;
  storefrontId?: string | null;
  returnTo: string;
  startedAt: string;
};

type WorkbenchSessionContextValue = {
  session: WorkbenchSession | null;
  /** True only for TnbInternal users with an active session. */
  isTesting: boolean;
  startWorkbenchSession: (session: Omit<WorkbenchSession, "startedAt">) => void;
  stopWorkbenchSession: () => void;
  record: (event: Omit<WorkbenchSessionEvent, "at">) => void;
  /** Returns and clears the events captured since the last chat message. */
  drain: () => WorkbenchSessionEvent[];
  pendingEventCount: number;
  lastRoute: string | null;
  isChatOpen: boolean;
  setChatOpen: (open: boolean) => void;
  hasUnread: boolean;
  setHasUnread: (unread: boolean) => void;
};

const WorkbenchSessionContext = createContext<WorkbenchSessionContextValue>({
  session: null,
  isTesting: false,
  startWorkbenchSession: () => {},
  stopWorkbenchSession: () => {},
  record: () => {},
  drain: () => [],
  pendingEventCount: 0,
  lastRoute: null,
  isChatOpen: false,
  setChatOpen: () => {},
  hasUnread: false,
  setHasUnread: () => {},
});

function readJson<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function loadSession(): WorkbenchSession | null {
  const parsed = readJson<Partial<WorkbenchSession>>(SESSION_KEY);
  if (!parsed?.flagKey || !parsed.chatId || !parsed.returnTo) return null;
  return {
    flagKey: parsed.flagKey,
    featureName: parsed.featureName ?? parsed.flagKey,
    chatId: parsed.chatId,
    organizationId: parsed.organizationId ?? null,
    organizationName: parsed.organizationName ?? null,
    storefrontId: parsed.storefrontId ?? null,
    returnTo: parsed.returnTo,
    startedAt: parsed.startedAt ?? new Date().toISOString(),
  };
}

function stringifyArg(arg: unknown): string {
  if (arg instanceof Error) return arg.message;
  if (typeof arg === "string") return arg;
  try {
    return JSON.stringify(arg);
  } catch {
    return String(arg);
  }
}

export const WorkbenchSessionProvider = ({ children }: PropsWithChildren) => {
  const { dbUser } = useAppContext();
  const location = useLocation();
  const [session, setSession] = useState<WorkbenchSession | null>(loadSession);
  const [events, setEvents] = useState<WorkbenchSessionEvent[]>(
    () => readJson<WorkbenchSessionEvent[]>(EVENTS_KEY) ?? [],
  );
  const [lastRoute, setLastRoute] = useState<string | null>(null);
  const [isChatOpen, setChatOpen] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const eventsRef = useRef(events);
  eventsRef.current = events;

  const isTnbInternal = dbUser?.userType === EnumUserUserType.TnbInternal;
  const isTesting = !!session && isTnbInternal;

  useEffect(() => {
    if (dbUser && !isTnbInternal && session) {
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(EVENTS_KEY);
      setSession(null);
      setEvents([]);
    }
  }, [dbUser, isTnbInternal, session]);

  const persistEvents = (next: WorkbenchSessionEvent[]) => {
    sessionStorage.setItem(EVENTS_KEY, JSON.stringify(next));
    return next;
  };

  const record = useCallback(
    (event: Omit<WorkbenchSessionEvent, "at">) => {
      if (!isTesting) return;
      setEvents(prev =>
        persistEvents(
          [...prev, { ...event, at: new Date().toISOString() }].slice(-MAX_EVENTS),
        ),
      );
    },
    [isTesting],
  );

  const drain = useCallback(() => {
    const current = eventsRef.current;
    setEvents(persistEvents([]));
    return current;
  }, []);

  const startWorkbenchSession = useCallback(
    (next: Omit<WorkbenchSession, "startedAt">) => {
      const full = { ...next, startedAt: new Date().toISOString() };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(full));
      setSession(full);
      setEvents(persistEvents([]));
    },
    [],
  );

  const stopWorkbenchSession = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    sessionStorage.removeItem(EVENTS_KEY);
    setSession(null);
    setEvents([]);
    setChatOpen(false);
    setHasUnread(false);
  }, []);

  const routeKey = `${location.pathname}${location.search}`;
  useEffect(() => {
    if (!isTesting) return;
    setLastRoute(routeKey);
    record({ type: "route", route: routeKey });
  }, [isTesting, routeKey, record]);

  useEffect(() => {
    if (!isTesting) return;
    const unsubscribe = onWorkbenchEvent(event => record(event));
    const onError = (e: ErrorEvent) =>
      record({ type: "consoleError", message: e.message });
    const onRejection = (e: PromiseRejectionEvent) =>
      record({ type: "consoleError", message: stringifyArg(e.reason) });
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    const originalConsoleError = console.error;
    console.error = (...args: unknown[]) => {
      const message = args.map(stringifyArg).join(" ").slice(0, 500);
      // React logs some errors during render; recording synchronously would set state mid-render.
      setTimeout(() => record({ type: "consoleError", message }), 0);
      originalConsoleError(...args);
    };
    return () => {
      unsubscribe();
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      console.error = originalConsoleError;
    };
  }, [isTesting, record]);

  const value = useMemo(
    () => ({
      session: isTesting ? session : null,
      isTesting,
      startWorkbenchSession,
      stopWorkbenchSession,
      record,
      drain,
      pendingEventCount: isTesting ? events.length : 0,
      lastRoute,
      isChatOpen,
      setChatOpen,
      hasUnread,
      setHasUnread,
    }),
    [
      session,
      isTesting,
      startWorkbenchSession,
      stopWorkbenchSession,
      record,
      drain,
      events.length,
      lastRoute,
      isChatOpen,
      hasUnread,
    ],
  );

  return (
    <WorkbenchSessionContext.Provider value={value}>
      {children}
    </WorkbenchSessionContext.Provider>
  );
};

export const useWorkbenchSession = () => useContext(WorkbenchSessionContext);
